import { useForm } from "@tanstack/react-form";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ArrowLeft, LoaderCircle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/ui/link";
import { Typography } from "@/components/ui/typography";
import { useTwoFactorMutation } from "@/features/auth/hooks/use-2fa-mutation";
import { twoFactorSchema } from "@/features/auth/schema/2fa.schema";
import { useAuthStore } from "@/features/auth/store/useAuthStore";

export const Route = createFileRoute("/auth/root-2-fa")({
  beforeLoad: async () => {
    const apiKey = useAuthStore.getState().apiKey;
    if (!apiKey) throw redirect({ to: "/auth/login" });
  },
  component: RouteComponent,
});

const defaultValues = {
  code: "",
};

function RouteComponent() {
  const { mutate: twoFactorMutate, isPending } = useTwoFactorMutation();

  const form = useForm({
    defaultValues,
    validators: {
      onChange: twoFactorSchema,
    },
    onSubmit: async ({ value }) => {
      twoFactorMutate(value);
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <Card className="w-full max-w-[95vw] sm:max-w-lg border-0 shadow-lg">
      <CardHeader className="space-y-2 text-center">
        <div className="mb-4 flex justify-center">
          <div className="rounded-full bg-purple-100 p-3">
            <Shield className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <CardTitle className="text-xl font-semibold">輸入驗證碼</CardTitle>
        <CardDescription className="text-gray-600">請至 Slack 確認當前有效的驗證碼</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <form onSubmit={onSubmit} className="space-y-6">
          <form.Field
            name="code"
            children={(field) => (
              <div className="flex flex-col items-center space-y-4">
                <Input
                  type="text"
                  placeholder="請輸入驗證碼"
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                  }}
                  maxLength={6}
                  className="text-center font-mono text-lg tracking-widest"
                />
              </div>
            )}
          />

          <Button type="submit" className="w-full" variant="default" disabled={isPending}>
            {isPending ? (
              <div className="flex items-center space-x-2">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                <Typography>驗證中...</Typography>
              </div>
            ) : (
              <>繼續</>
            )}
          </Button>
        </form>

        <Link to="/auth/login">
          <ArrowLeft className="h-4 w-4" />
          <span>返回登入頁面</span>
        </Link>
      </CardContent>
    </Card>
  );
}
